/**
 * What opens over the app before anything else, for a person signed in:
 *
 *  1. the Terms of Use, while they have not accepted the version in force — blocking; then
 *  2. the first-run guide, once per person on this device: "beginner or experienced?". An
 *     experienced person goes straight to the app; a beginner says what they want (docs/refactor/51,
 *     N2). To use AI day to day: the app and an AI, nothing else, and Modo simples switched on (it is
 *     changed back in Configurações › Perfil). To program: the short list of what they need outside
 *     Vephus (GitHub and Git, Claude and Claude Code, Docker, the desktop app) and a button into the
 *     step-by-step setup, or — the setup done — into Iniciar projeto, from an idea.
 *
 * The guide can be opened again from the sidebar (openFirstRunGuide). Same bundle in the browser and
 * the desktop app; the links open with openExternal, a new tab in one and the system browser in the
 * other, and the desktop app is not offered to somebody already using it.
 */

import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Code2, Download, ExternalLink, GraduationCap, Rocket, Sparkles, Wand2 } from 'lucide-react'
import { api } from '../lib/api'
import { isDesktop, openExternal } from '../lib/desktop'
import { useI18n } from '../lib/i18n'
import {
  guideAnswer,
  guideItems,
  onOpenFirstRunGuide,
  rememberGuideAnswer,
  setupPath,
  type GuideAnswer,
  type GuideTrack,
} from '../lib/firstRunGuide'
import { setSimpleMode } from '../lib/simpleMode'
import { DOWNLOAD_URL } from '../lib/startPath'
import { Modal } from './Modal'
import { TermsGate } from './TermsOfUse'

type GuideStep = 'closed' | 'ask' | 'track' | GuideTrack

export function FirstRunLayer() {
  const location = useLocation()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const [step, setStep] = useState<GuideStep>('closed')

  const userId = me?.userId ?? null
  const person = Boolean(me && !me.isAgent && userId)
  // Undefined (an API from before the terms) is not a refusal: only an explicit false asks.
  const termsPending = person && me?.termsAccepted === false

  // Asked once the terms are out of the way, and only if this person has not answered here before.
  useEffect(() => {
    // Not in kids mode (docs/refactor/48): the adult set the account up; the child starts at the ideas.
    if (person && !termsPending && userId && !me?.kidsMode && guideAnswer(userId) === null) setStep('ask')
  }, [person, termsPending, userId, me?.kidsMode])

  // Reopened from the sidebar: what they want to do, then its list.
  useEffect(() => onOpenFirstRunGuide(() => setStep('track')), [])

  // Stable, because the dialog re-runs its focus handling whenever this changes.
  const close = useCallback(() => {
    // Closing without answering counts as an answer: it was seen, and it is in the sidebar.
    if (userId && guideAnswer(userId) === null) rememberGuideAnswer(userId, 'experienced')
    setStep('closed')
  }, [userId])

  if (!person || !userId) return null
  // The terms page itself stays readable, or the link in the dialog would lead back to the dialog.
  if (termsPending) return location.pathname === '/terms' ? null : <TermsGate />
  if (step === 'closed') return null

  const answer = (choice: GuideAnswer): void => {
    rememberGuideAnswer(userId, choice)
    setStep(choice === 'beginner' ? 'track' : 'closed')
  }

  const pick = (track: GuideTrack): void => {
    // To use AI with no programming: Modo simples, which the person can switch off in Perfil.
    setSimpleMode(userId, track === 'use')
    setStep(track)
  }

  return (
    <FirstRunGuide
      step={step}
      onAnswer={answer}
      onTrack={pick}
      onClose={close}
    />
  )
}

function FirstRunGuide({ step, onAnswer, onTrack, onClose }: {
  step: 'ask' | 'track' | GuideTrack
  onAnswer: (answer: GuideAnswer) => void
  onTrack: (track: GuideTrack) => void
  onClose: () => void
}) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const desktop = isDesktop()

  return (
    <Modal title={t('guide.title')} onClose={onClose} wide>
      {step === 'ask' ? (
        <div className="stack">
          <p style={{ margin: 0 }}>{t('guide.question')}</p>
          <div className="guide-choices">
            <button type="button" className="guide-choice" onClick={() => onAnswer('beginner')}>
              <GraduationCap size={20} aria-hidden className="guide-choice__icon" />
              <span className="guide-choice__title">{t('guide.beginner')}</span>
              <span className="guide-choice__hint tiny">{t('guide.beginnerHint')}</span>
            </button>
            <button type="button" className="guide-choice" onClick={() => onAnswer('experienced')}>
              <Rocket size={20} aria-hidden className="guide-choice__icon" />
              <span className="guide-choice__title">{t('guide.experienced')}</span>
              <span className="guide-choice__hint tiny">{t('guide.experiencedHint')}</span>
            </button>
          </div>
        </div>
      ) : step === 'track' ? (
        <div className="stack">
          <p style={{ margin: 0 }}>{t('guide.trackQuestion')}</p>
          <div className="guide-choices">
            <button type="button" className="guide-choice" onClick={() => onTrack('use')}>
              <Wand2 size={20} aria-hidden className="guide-choice__icon" />
              <span className="guide-choice__title">{t('guide.track.use')}</span>
              <span className="guide-choice__hint tiny">{t('guide.track.useHint')}</span>
            </button>
            <button type="button" className="guide-choice" onClick={() => onTrack('code')}>
              <Code2 size={20} aria-hidden className="guide-choice__icon" />
              <span className="guide-choice__title">{t('guide.track.code')}</span>
              <span className="guide-choice__hint tiny">{t('guide.track.codeHint')}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="stack">
          <p className="small" style={{ margin: 0 }}>{t('guide.needs')}</p>
          <ol className="guide-list" aria-label={t('guide.needs')}>
            {guideItems(desktop, step).map((item, index) => (
              <li key={item.id} className="guide-item" data-testid={`guide-item-${item.id}`}>
                <span className="setup-step__mark" aria-hidden>{index + 1}</span>
                <div className="guide-item__text">
                  <span className="guide-item__title">{t(`guide.item.${item.id}.title`)}</span>
                  <span className="tiny guide-item__body">{t(`guide.item.${item.id}.body`)}</span>
                  <ul className="guide-item__links">
                    {item.links.map((link) => (
                      <li key={link.url} className="guide-link">
                        <span className="small">{t(link.labelKey)}</span>
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => void openExternal(link.url)}
                          aria-label={`${t('guide.go')}: ${t(link.labelKey)}`}
                        >
                          {t('guide.go')} <ExternalLink size={12} aria-hidden />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ol>
          {step === 'use' ? (
            <>
              <p className="small muted guide-note">{t('guide.useNext')}</p>
              <div className="row guide-actions">
                <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.close')}</button>
                {desktop ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => {
                      onClose()
                      navigate(setupPath(desktop))
                    }}
                  >
                    {t('guide.connectAi')} <ArrowRight size={14} aria-hidden />
                  </button>
                ) : (
                  <button type="button" className="btn btn--primary" onClick={() => void openExternal(DOWNLOAD_URL)}>
                    <Download size={14} aria-hidden /> {t('guide.link.desktop')}
                  </button>
                )}
              </div>
            </>
          ) : (
          <>
          <p className="small muted guide-note">{t('guide.next')}</p>
          <div className="row guide-actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.close')}</button>
            {/* With the setup done, a beginner's next step is the project itself: from an idea, in a conversation. */}
            <button
              type="button"
              className="btn"
              onClick={() => {
                onClose()
                navigate('/start-project')
              }}
            >
              <Sparkles size={14} aria-hidden /> {t('guide.startProject')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                onClose()
                navigate(setupPath(desktop))
              }}
            >
              {t('guide.startSetup')} <ArrowRight size={14} aria-hidden />
            </button>
          </div>
          </>
          )}
        </div>
      )}
    </Modal>
  )
}
